import { type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import type { DonationRow, ModelOption } from '../lib/donations';
import { donationResourceLabel, donationResourceRef, donationStatusKey } from '../lib/donationView';
import { absolute } from '../lib/time';
import { DonationModelPicker } from './DonationModelPicker';
import { StatusPill, type StatusTone } from './StatusPill';
import { Button } from './ui/Button';

// One gift the caller's projects RECEIVED (arc 101 slice 11, plan doc 05).
// Presenter — the route owns the reads, both writes and their failures.
//
// Three rules the card exists to hold:
//
//   - **The donor is a LOGIN.** Never an email, never anything else: the only
//     donor identity this surface carries is the one that is already public on
//     GitHub, and an unresolved donor reads as an anonymous backer rather than a
//     blank. A gift is not a contact list.
//   - **A parked gift is SHOWN, with what we can stand behind.** Capacity that
//     vanishes silently is the failure this inbox exists to prevent, so a parked
//     row states when it stopped and the causes that park a gift; a pending
//     `money` row says it is waiting on payment instead.
//   - **Steering is only offered on a LIVE gift.** The API refuses a pick on a
//     parked row (its write re-asserts liveness), so a picker there would be a
//     control that can only fail.

const STATUS_TONE: Record<string, StatusTone> = {
  'donations.status_live': 'success',
  'donations.status_pending': 'warning',
  'donations.status_parked': 'neutral',
};

export type DonationReceivedCardProps = {
  readonly row: DonationRow;
  /** The platform model catalog, off the same page as the rows. */
  readonly catalog: readonly ModelOption[];
  /** Whether this caller may write (the `canManageDonations` mirror). */
  readonly offered: boolean;
  /** True while THIS gift's model save or decline is in flight. */
  readonly busy: boolean;
  readonly onChoose: (row: DonationRow, model: string | null) => void;
  readonly onDecline: (row: DonationRow) => void;
};

export function DonationReceivedCard(props: DonationReceivedCardProps): JSX.Element {
  const donor = (): string => props.row.donor_login ?? t('donations.donor_unknown');
  const status = (): string => donationStatusKey(props.row);

  return (
    <li class="card donation-gift" data-kind={props.row.kind}>
      {/* divs, not header/footer: outside sectioning content those map to
          banner/contentinfo and duplicate the shell's landmarks (axe). */}
      <div class="card__header">
        <h2 class="card__title">{t('donations.donor_from', { donor: donor() })}</h2>
        <div class="donation-gift__badges">
          <span class="badge badge--outline">{t(`donations.kind_${props.row.kind}`)}</span>
          <StatusPill tone={STATUS_TONE[status()] ?? 'neutral'}>{t(status())}</StatusPill>
        </div>
        <p class="card__desc">
          <Show
            when={props.row.target_repo_full_name}
            fallback={t('donations.card_project_unnamed')}
          >
            {(name) => t('donations.card_project', { project: name() })}
          </Show>
        </p>
      </div>

      <div class="card__content donation-gift__meta">
        <span>{t('donations.card_given', { when: absolute(props.row.created_iso) })}</span>
        <span class="data-table__mono" title={donationResourceRef(props.row)}>
          {donationResourceLabel(props.row)}
        </span>
      </div>

      <Show when={props.row.revoked_iso}>
        {(when) => (
          <p class="card__content field__desc" role="status">
            {t('donations.parked_reason', { when: absolute(when()) })}
          </p>
        )}
      </Show>
      <Show when={!props.row.active && props.row.revoked_iso === null}>
        <p class="card__content field__desc" role="status">
          {t('donations.pending_reason')}
        </p>
      </Show>

      <Show when={props.row.active}>
        <div class="card__content">
          <DonationModelPicker
            row={props.row}
            catalog={props.catalog}
            offered={props.offered}
            busy={props.busy}
            onChoose={(model) => props.onChoose(props.row, model)}
          />
        </div>
        <Show when={props.offered}>
          <div class="card__footer donation-gift__actions">
            <Button
              variant="ghost"
              size="sm"
              disabled={props.busy}
              aria-label={t('donations.decline_aria', {
                kind: t(`donations.kind_${props.row.kind}`),
                donor: donor(),
              })}
              onClick={() => props.onDecline(props.row)}
            >
              {t('donations.decline')}
            </Button>
          </div>
        </Show>
      </Show>
    </li>
  );
}
